/**
 * Filter module constants for Widget Integration.
 *
 * This file contains all constant values used throughout the filter widget,
 * including storage keys, CSS selectors, URL parameters, and configuration defaults.
 *
 * @remarks
 * Technical Support teams can reference these constants when customizing filter
 * behavior or need to understand internal filter widget structure.
 *
 * @packageDocumentation
 */

/**
 * Storage key for tracking the "All Products" collection ID.
 *
 * Used to store the special collection ID that represents all products
 * in local storage for session persistence.
 */
export const COLLECTION_ALL_STORAGE_KEY = 'tae_collection_all_id';

/**
 * Default separator for joining multiple filter values in URLs.
 *
 * Used when constructing URL parameters with multiple selected values
 * (e.g., "red,blue,green" for color filters).
 */
export const DEFAULT_SEPARATOR = ',';

/**
 * Regular expression pattern for escaping special regex characters.
 *
 * Used to safely escape user input before using it in regular expressions
 * to prevent regex injection vulnerabilities.
 */
export const REGEX_ESCAPE_PATTERN = /[.*+?^${}()|[\]\\]/g;

/**
 * Prefix for all Boost filter URL parameters.
 *
 * All filter parameters in the URL start with this prefix to avoid
 * conflicts with other query parameters (e.g., "pf_st_color", "pf_t_vendor").
 */
export const FILTER_KEY_PREFIX = 'pf_';

/**
 * Storage key for current collection ID in session storage.
 *
 * Stores the active collection ID to maintain state across page navigations
 * and filter operations.
 */
export const BOOST_SD_COLLECTION_ID_KEY = 'boost_sd_collection_id_key';

/**
 * Storage key for current collection tags in session storage.
 *
 * Stores collection tags to support tag-based filtering within collections.
 */
export const BOOST_SD_COLLECTION_TAGS_KEY = 'boost_sd_collection_tags_key';

/**
 * Keyboard keys that trigger filter option selection.
 *
 * Array of key values that should activate filter options when focused,
 * supporting keyboard accessibility (Enter and Space).
 */
export const KEYS = ['Enter', ' ', 'Spacebar'];
/**
 * Storage key for tracking the initial page number.
 *
 * Stores the starting page when pagination is used, allowing reset to initial state.
 */
export const INITIAL_PAGE = 'boostSDInitialPage';

/**
 * Storage key for tracking the previous page URL/state.
 *
 * Used for back button functionality in pagination to restore previous page state.
 */
export const PREV_PAGE = 'boostSDCurrentPreviousPage';

/**
 * Storage key for tracking the next page URL/state.
 *
 * Used for forward button functionality in pagination to load next page state.
 */
export const NEXT_PAGE = 'boostSDCurrentNextPage';

/**
 * Filter tree UI state storage keys.
 *
 * Keys used to persist filter tree open/closed states and focus information
 * in session storage for consistent user experience across page loads.
 *
 * @remarks
 * Technical Support teams can use these keys to access or modify filter tree
 * state in custom implementations.
 *
 * @example
 * ```typescript
 * const isMobileOpen = sessionStorage.getItem(FILTER_TREE_STATE_KEY.MOBILE_OPENING);
 * if (isMobileOpen === 'true') {
 *   // Mobile filter tree is currently open
 * }
 * ```
 */
export const FILTER_TREE_STATE_KEY = {
  /** Key for mobile filter tree open/closed state */
  MOBILE_OPENING: 'filterTreeMobileOpening',
  /** Key for desktop filter tree open/closed state */
  DESKTOP_OPENING: 'filterTreeDesktopOpening',
  /** Key for currently focused filter option ID */
  CURRENT_FOCUS: 'filterCurrentFocus',
  /** Key for last pressed keyboard key code */
  KEY_CODE: 'filterKeyCode',
} as const;

/**
 * Widget placement locations for analytics and behavior customization.
 *
 * Defines where the filter widget is rendered on the site, affecting
 * analytics tracking and behavior patterns.
 *
 * @remarks
 * Technical Support teams can use these values to customize widget behavior
 * based on placement context.
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
 *   return class extends FilterAPI {
 *     async getProductByFilter(params, additions) {
 *       // Add placement-specific parameter
 *       const placement = this.getPlacement();
 *       if (placement === PLACEMENT.product_page) {
 *         params.show_related = true;
 *       }
 *       return super.getProductByFilter(params, additions);
 *     }
 *   };
 * });
 * ```
 */
export const PLACEMENT = {
  /** Product detail page placement */
  product_page: 'product_page',
  /** Search results page placement */
  search_page: 'search_page',
  /** Instant search dropdown placement */
  instant_search: 'instant_search',
} as const;

/**
 * Storage key for previous request IDs to prevent duplicate requests.
 *
 * Stores recent API request IDs to detect and cancel redundant calls.
 */
export const PRE_REQUEST_IDS = 'boostSdPreRequestIds';

/**
 * Storage key for previous user action.
 *
 * Tracks the last filter action taken by the user for analytics and state management.
 */
export const PRE_ACTION = 'boostSdPreAction';

/**
 * CSS selector for mobile filter tree wrapper element.
 *
 * Targets the main container for the mobile filter tree overlay.
 */
export const FILTER_MOBILE_WRAPPER_SELECTOR = '#boost-sd_filter-tree-mobile';

/**
 * CSS class for full-height filter option wrapper on mobile.
 *
 * Applied to filter option containers that should take full viewport height.
 */
export const FILTER_MOBILE_FULL_HEIGHT_CLASS = 'boost-sd__filter-option-itemwrapper--full-height';

/**
 * URL parameter key for tag filters.
 *
 * The query parameter name used for tag-based filtering (e.g., "?pf_tag=summer").
 */
export const FILTER_TAG_KEY = 'pf_tag';

/**
 * CSS selector for desktop filter tree wrapper element.
 *
 * Targets the left sidebar container for desktop filter display.
 */
export const FILTER_DESKTOP_WRAPPER_SELECTOR = '.boost-sd-left';

/**
 * CSS selector combining full-height class for mobile filter wrapper.
 */
export const FILTER_MOBILE_FULL_HEIGHT_SELECTOR = `.${FILTER_MOBILE_FULL_HEIGHT_CLASS}`;

/**
 * Available filter option types.
 *
 * Defines all supported filter types in the filter widget. Each type has
 * specific behavior, display format, and data structure requirements.
 *
 * @remarks
 * Technical Support teams can use these constants when checking filter types
 * in custom implementations or when extending FilterAPI to handle specific
 * filter types differently.
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
 *   return class extends FilterHelper {
 *     parseFilterTreeToParams(options) {
 *       const params = super.parseFilterTreeToParams(options);
 *       // Custom logic for color filters
 *       options.forEach(option => {
 *         if (option.filterType === FILTER_OPTION_TYPE.COLOR) {
 *           // Add custom color parameter
 *           params.custom_color_mode = 'enhanced';
 *         }
 *       });
 *       return params;
 *     }
 *   };
 * });
 * ```
 */
export const FILTER_OPTION_TYPE = {
  /** Collection/category filter */
  COLLECTION: 'collection',
  /** Product vendor/brand filter */
  VENDOR: 'vendor',
  /** Product type filter */
  PRODUCT_TYPE: 'product_type',
  /** Product title/name filter */
  TITLE: 'title',
  /** Product price filter */
  PRICE: 'price',
  /** Variant-specific price filter */
  VARIANTS_PRICE: 'variants_price',
  /** Sale percentage discount filter */
  PERCENT_SALE: 'percent_sale',
  /** Product review rating filter */
  REVIEW_RATINGS: 'review_ratings',
  /** Product weight filter */
  WEIGHT: 'weight',
  /** Stock availability filter */
  STOCK: 'stock',
  /** Product tag filter */
  TAG: 'tag',
  /** Hierarchical multi-level tag filter */
  MULTI_LEVEL_TAG: 'multi_level_tag',
  /** Store location filter */
  LOCATION: 'location',
  /** Product color option filter */
  COLOR: 'opt_color',
  /** Product size option filter */
  SIZE: 'opt_size',
  /** Product category taxonomy filter */
  PRODUCT_CATEGORY: 'product_category',
} as const;

/**
 * Filter option display type variants.
 *
 * Defines how filter options are visually rendered in the UI. Each display type
 * has different HTML structure and styling requirements.
 *
 * @remarks
 * Technical Support teams can check display types when customizing filter option
 * rendering or styling per shop.
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
 *   return class extends FilterHandler {
 *     handleClickProductItem(e, actionId) {
 *       const state = this.filterStore.getState();
 *       const option = state.optionsMap[actionId];
 *       if (option?.displayType === FILTER_OPTION_DISPLAY_TYPE.SWATCH) {
 *         // Custom analytics for swatch clicks
 *         this.trackSwatchInteraction(actionId);
 *       }
 *       return super.handleClickProductItem(e, actionId);
 *     }
 *   };
 * });
 * ```
 */
export const FILTER_OPTION_DISPLAY_TYPE = {
  /** Vertical list display with checkboxes/labels */
  LIST: 'list',
  /** Grid/box display with larger clickable areas */
  BOX: 'box',
  /** Range slider for numeric values (price, weight, etc.) */
  RANGE: 'range',
  /** Color/image swatch display for visual options */
  SWATCH: 'swatch',
  /** Star rating display for review filters */
  RATING: 'rating',
  /** Hierarchical sub-category tree display */
  SUB_CATEGORY: 'sub_category',
  /** Multi-level expandable collection hierarchy */
  MULTI_LEVEL_COLLECTIONS: 'multi_level_collections',
  /** Multi-level expandable tag hierarchy */
  MULTI_LEVEL_TAG: 'multi_level_tag',
} as const;

export const FILTER_OPTION_SELECTOR = '.boost-sd__filter-option';

/**
 * Template identifier mapping for filter widget components.
 *
 * Maps template keys to their corresponding template IDs in the template system.
 * Used by FilterHelper.getTemplate() to retrieve the correct HTML template.
 *
 * @remarks
 * Technical Support teams can reference these template names when customizing
 * filter widget rendering or when overriding templates for specific shops.
 */
export const TEMPLATE_MAPPING = {
  /** Template for the "Refine By" selected filters bar */
  refineByTemplate: 'refineBy',
  /** Template for list-style filter option items */
  filterOptionListTemplate: 'filterOptionListItem',
  /** Template for box/grid-style filter option items */
  filterOptionBoxTemplate: 'filterOptionBoxItem',
  /** Template for color/image swatch filter items */
  filterOptionSwatchTemplate: 'filterOptionSwatchItem',
  /** Template for multi-level collection hierarchy items */
  filterOptionMultiLevelCollection: 'filterOptionMultiLevelCollectionItem',
  /** Template for multi-level tag hierarchy items */
  filterOptionMultiLevelTagTemplate: 'filterOptionMultiLevelTagItem',
  /** Template for star rating filter items */
  filterOptionRatingTemplate: 'filterOptionRatingItem',
  /** Template for cart display */
  cartTemplate: 'cart',
  /** Template for product price display */
  productPriceTemplate: 'productPrice',
  /** Template for search tab collections content */
  searchTabCollectionsTemplate: 'searchTabCollectionsContent',
  /** Template for search tab pages content */
  searchTabPagesTemplate: 'searchTabPagesContent',
  /** Template for search tab pagination controls */
  searchTabPaginationTemplate: 'searchTabCollectionsContentPagination',
  /** Template for pre-order functionality */
  preOrderTemplate: 'preOrder',
} as const;

/**
 * Filter option "Show More" behavior types.
 *
 * Defines how filter options are displayed when there are many values,
 * controlling the expansion/collapse mechanism.
 *
 * @remarks
 * Used in ViewMoreState to determine how users can access additional filter values.
 */
export const SHOW_MORE_TYPES = {
  /** Display all values without "Show More" functionality */
  DISPLAY_ALL_VALUES: 'none',
  /** Show limited values with "View More" button to expand */
  VIEW_MORE: 'viewmore',
  /** Show values in scrollable container with scrollbar */
  SCROLL_BAR: 'scrollbar',
  VIEW_MORE_SCROLL_BAR: 'viewmore_scrollbar',
} as const;

export const FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS =
  'boost-sd__filter-option-itemwrapper--hidden';
export const FILTER_OPTION_SEARCH_OPTION_SELECTOR = '.boost-sd__filter-option-search-option';
export const FILTER_OPTION_ITEM_WRAPPER_COLLAPSED_CLASS =
  'boost-sd__filter-option-itemwrapper--collapsed';
export const FILTER_OPTION_TITLE_OPENING_CLASS = 'boost-sd__filter-option-title--opening';

export const VIEW_MORE_CLICK_DEBOUNCE_TIME = 500;
export const FILTER_TREE_TOGGLE_BUTTON_SELECTOR = '.boost-sd__filter-tree-toggle-button';
export const FILTER_MOBILE_ICON_SELECTOR = '#boost-sd__filter-tree-toggle-button';
export const FILTER_TREE_MOBILE_BUTTON_CLEAR =
  '.boost-sd__filter-tree-vertical-mobile-toolbar #boost-sd__button-toolbar-mobile-clear';

export const FILTER_MOBILE_BUTTON_CLOSE_CLASS =
  '.boost-sd__filter-tree-vertical-mobile-toolbar .boost-sd__button--close';
export const FILTER_MOBILE_BUTTON_BACK_CLASS =
  '.boost-sd__filter-tree-vertical-mobile-toolbar .boost-sd__button--back';
export const FILTER_MOBILE_BUTTON_HIDDEN = 'boost-sd__button--hidden';
export const FILTER_MOBILE_BUTTON_BACK = 'boost-sd__button--back';
export const FILTER_MOBILE_BUTTON_CLOSE = 'boost-sd__button--close';

export const FILTER_TREE_MOBILE_TOOL_BAR = '.boost-sd__filter-tree-vertical-mobile-toolbar';

export const FILTER_OPTION_CONTENT_INNER = 'boost-sd__filter-option-content-inner';
export const FILTER_OPTION_CONTENT_INNER_SCROLL_BAR =
  'boost-sd__filter-option-content-inner--scrollbar';

export const SWATCH_PREFIX = 'pfs-swatch-';

export const FILTER_TREE_RESET_DELAY = 100;
export const LIMIT_FIRST_LOAD = 30;

export const TOOLBAR_CONTENT_SELECTOR = '.boost-sd__toolbar-content';

// Filter Loading
export const LOADING_FILTER_ICON_ID = 'boost-sd-loading-icon-filter';

/**
 * Standard URL query parameter names for filter API requests.
 *
 * Defines the parameter keys used when making requests to the Boost filter API.
 * Technical Support teams can use these when building custom filter parameters.
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
 *   return class extends FilterAPI {
 *     protected buildParams(params) {
 *       const result = super.buildParams(params);
 *       // Add custom timestamp parameter
 *       result[QUERY_PARAM.TIMESTAMP] = Date.now();
 *       return result;
 *     }
 *   };
 * });
 * ```
 */
export const QUERY_PARAM = {
  /** Search query string parameter */
  QUERY: 'q',
  /** Collection/product tag filter parameter */
  TAG: 'tag',
  /** Collection scope identifier for filtered results */
  COLLECTION_SCOPE: 'collection_scope',
  /** Request timestamp for cache busting */
  TIMESTAMP: 't',
  /** Session ID for tracking user session */
  SESSION_ID: 'sid',
  /** Sort-first flag for prioritizing certain products */
  SORT_FIRST: 'sort_first',
  /** Filter by product-level availability */
  PRODUCT_AVAILABLE: 'product_available',
  /** Filter by variant-level availability */
  VARIANT_AVAILABLE: 'variant_available',
} as const;

/**
 * API response format types.
 *
 * Specifies whether the filter API should return JSON data or pre-rendered HTML.
 *
 * @remarks
 * Use JSON for programmatic processing and HTML for direct DOM injection.
 * Most shops use JSON format for better flexibility and client-side rendering control.
 *
 * @example
 * Customize response type based on shop requirements:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
 *   return class extends FilterAPI {
 *     async getProductByFilter(params, additions) {
 *       // Force JSON response for custom processing
 *       const customAdditions = {
 *         ...additions,
 *         responseType: RESPONSE_TYPE.JSON
 *       };
 *       return super.getProductByFilter(params, customAdditions);
 *     }
 *   };
 * });
 * ```
 */
export const RESPONSE_TYPE = {
  /** JSON response with structured data */
  JSON: 'json',
  /** HTML response with pre-rendered markup */
  HTML: 'html',
} as const;

/**
 * Pagination behavior types for filter API requests.
 *
 * Defines how new results should be integrated with existing content when paginating.
 *
 * @remarks
 * Used in FilterAPIAdditionParams to control result loading behavior.
 * - MORE: Appends results for infinite scroll (most common)
 * - PREVIOUS: Prepends results for bidirectional scroll
 * - REPLACE: Replaces all results for traditional pagination
 *
 * @example
 * Implement custom infinite scroll behavior:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
 *   return class extends FilterHandler {
 *     async loadMoreProducts() {
 *       const additions = {
 *         behavior: BEHAVIOR_TYPE.MORE,
 *         page: this.getCurrentPage() + 1
 *       };
 *       return this.filterAPI.getProductByFilter(this.currentParams, additions);
 *     }
 *   };
 * });
 * ```
 */
export const BEHAVIOR_TYPE = {
  /** Append results to end (infinite scroll down) */
  MORE: 'more',
  /** Prepend results to beginning (scroll up) */
  PREVIOUS: 'previous',
  /** Replace all existing results */
  REPLACE: 'replace',
} as const;

/**
 * API action types for different filter widget operations.
 *
 * Categorizes the type of API request being made for analytics and routing.
 *
 * @remarks
 * Used to track user actions in analytics platforms and to route requests
 * to appropriate API endpoints. Technical Support teams can use these values
 * when implementing custom analytics tracking.
 *
 * @example
 * Track action types in analytics:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
 *   return class extends FilterAPI {
 *     async getProductByFilter(params, additions) {
 *       // Track filter action
 *       if (window.gtag && additions.actionType === API_ACTION_TYPE.FILTER) {
 *         window.gtag('event', 'filter_applied', {
 *           filters: Object.keys(params).filter(k => k.startsWith('pf_'))
 *         });
 *       }
 *       return super.getProductByFilter(params, additions);
 *     }
 *   };
 * });
 * ```
 */
export const API_ACTION_TYPE = {
  /** Search query action */
  SEARCH: 'search',
  /** Filter selection action */
  FILTER: 'filter',
  /** Search suggestion/autocomplete action */
  SUGGEST: 'suggest',
} as const;

/**
 * Standard CSS class names used in filter widget styling.
 *
 * Contains reusable class names for common UI states and components.
 * Technical Support teams can reference these when adding custom styles.
 *
 * @remarks
 * All classes follow BEM naming convention with "boost-sd__" prefix.
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
 *   return class extends FilterHandler {
 *     handleSort(e, actionId) {
 *       // Add custom animation class
 *       const element = e.target.closest(`.${CSS_CLASS.FILTER_OPTION_ITEM}`);
 *       element?.classList.add(CSS_CLASS.ANIMATION_FADE_IN);
 *       return super.handleSort(e, actionId);
 *     }
 *   };
 * });
 * ```
 */
export const CSS_CLASS = {
  /** Class to hide elements visually */
  HIDE: 'boost-sd__g-hide',
  /** Class to prevent page scrolling (used with modals) */
  NO_SCROLL: 'boost-sd__g-no-scroll',
  /** Combined classes for clear/reset buttons */
  BUTTON_CLEAR: 'boost-sd__button boost-sd__button--clear',
  /** Class for individual filter option items */
  FILTER_OPTION_ITEM: 'boost-sd__filter-option-item',
  /** Class for box-style filter option items */
  FILTER_OPTION_BOX_ITEM: 'boost-sd__filter-option-box-item',
  /** Class for selected box-style filter items */
  FILTER_OPTION_BOX_ITEM_SELECTED: 'boost-sd__filter-option-box-item--selected',
  /** Prefix for sorting-related classes */
  SORTING_PREFIX: 'boost-sd__sorting-',
  /** Class for slide-in animation */
  ANIMATE_SLIDE_IN: 'animate--slide-in',
  /** Class for fade-in animation */
  ANIMATION_FADE_IN: 'animation--fade-in',
} as const;

/**
 * CSS selectors for filter widget DOM elements.
 *
 * Pre-defined selectors for querying filter widget elements in the DOM.
 * Technical Support teams can use these for custom DOM manipulation.
 *
 * @remarks
 * These selectors target specific filter widget components and are used
 * throughout FilterHandler and FilterHelper for element access.
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
 *   return class extends FilterHandler {
 *     handleAction(e, actionId, metadata) {
 *       // Find and style the collection header
 *       const header = this.document.querySelector(DOM_SELECTOR.COLLECTION_HEADER);
 *       if (header) {
 *         header.style.borderBottom = '2px solid #333';
 *       }
 *       return super.handleAction(e, actionId, metadata);
 *     }
 *   };
 * });
 * ```
 */
export const DOM_SELECTOR = {
  /** Collection header element */
  COLLECTION_HEADER: '#boost-sd__collection-header',
  /** Desktop "Refine By" selected filters container */
  REFINE_BY_DESKTOP: 'div[class^="boost-sd__refine-by"]',
  /** Mobile "Refine By" selected filters container */
  REFINE_BY_MOBILE: '#bc-refine-by-mobile',
  /** Filter option title/header element */
  FILTER_OPTION_TITLE: '.boost-sd__filter-option-title',
  /** Expanded/open filter option title */
  FILTER_OPTION_TITLE_OPENING: '.boost-sd__filter-option-title--opening',
  /** Filter option label text element */
  FILTER_OPTION_LABEL: '.boost-sd__filter-option-label',
  /** Individual list item in filter options */
  FILTER_OPTION_LISTITEM: '.boost-sd__filter-option-listitem',
  /** Scroll to top button */
  SCROLL_TO_TOP: '.boost-sd__scroll-to-top',
  /** Mobile toolbar filter toggle button */
  TOOLBAR_TOP_MOBILE: '.boost-sd__toolbar-top-mobile .boost-sd__filter-tree-toggle-button',
  /** Expanded filter tree toggle button */
  FILTER_TREE_TOGGLE_EXPAND: '.boost-sd__filter-tree-toggle-button--expand',
  /** Focusable elements in toolbar */
  TOOLBAR_TABINDEX: '.boost-sd__toolbar [tabindex]',
  /** Range slider unit display element */
  FILTER_OPTION_RANGE_SLIDER_UNIT: '.boost-sd__filter-option-range-slider-unit',
  /** Meta tag for noindex directive */
  META_NOINDEX: 'meta[content="noindex,nofollow,nosnippet"]',
} as const;

/**
 * HTML attribute names used in filter widget elements.
 *
 * Standard attribute names for accessibility, data storage, and behavior.
 */
export const HTML_ATTR = {
  /** ARIA expanded state attribute for accessibility */
  ARIA_EXPANDED: 'aria-expanded',
  /** Tab index attribute for keyboard navigation */
  TABINDEX: 'tabindex',
  /** Data attribute for storing metadata JSON */
  DATA_METADATA: 'data-metadata',
  /** Data attribute for filter option ID */
  DATA_FILTER_OPTION_ID: 'data-filterOptionId',
  /** Data attribute for action identifier */
  DATA_ACTION: 'data-action',
  /** Generic metadata attribute */
  METADATA: 'metadata',
  /** Hyperlink reference attribute */
  HREF: 'href',
  /** Inline style attribute */
  STYLE: 'style',
} as const;

/**
 * Common HTML attribute values used in filter widget.
 *
 * Pre-defined values for standard attributes to ensure consistency.
 */
export const ATTR_VALUE = {
  /** ARIA expanded state: true */
  ARIA_EXPANDED_TRUE: 'true',
  /** ARIA expanded state: false */
  ARIA_EXPANDED_FALSE: 'false',
  /** Disabled tab index (element not keyboard focusable) */
  TABINDEX_DISABLED: '-1',
  /** Enabled tab index (element keyboard focusable) */
  TABINDEX_ENABLED: '0',
  /** Inline style for relative positioning */
  STYLE_POSITION_RELATIVE: 'position: relative',
  /** Inline style for right alignment */
  STYLE_RIGHT_ZERO: 'right: 0',
} as const;

/**
 * Pre-defined metadata JSON strings for common filter actions.
 *
 * Contains stringified JSON metadata used in data-metadata attributes.
 */
export const METADATA_JSON = {
  /** Metadata for clear all filters action */
  CLEAR_ALL_FILTER: '{ "action": { "clearAllFilter": "" }}',
} as const;

/**
 * Responsive breakpoints for mobile and tablet detection.
 *
 * Maximum pixel widths for device type classification.
 */
export const RESOLUTION_BREAKPOINT = {
  /** Maximum width for mobile devices (in pixels) */
  MOBILE_MAX: 575,
  /** Maximum width for tablet devices (in pixels) */
  TABLET_MAX: 991,
};

/**
 * Parallax scroll speed multiplier for visual effects.
 *
 * Controls the speed of parallax scrolling animations.
 */
export const PARALLAX_SPEED = 0.5;

/**
 * Initial scale factor for zoom animations.
 *
 * Starting scale value for image or element zoom effects.
 */
export const INITIAL_SCALE = 1.5;

/**
 * Storage key for current result limit/page size.
 *
 * Stores how many products per page are currently displayed.
 */
export const LIMIT = 'boostSDLimit';

/**
 * Storage key for limit setting configuration.
 *
 * Stores the configured page size options available to users.
 */
export const LIMIT_SETTING = 'boostSDLimitSetting';

/**
 * Storage key for currently selected product ID.
 *
 * Tracks which product is currently selected/focused.
 */
export const SELECTED_PRODUCT_ID = 'boostSDSelectedProductId';

/**
 * Storage key for currently selected collection.
 *
 * Stores the active collection identifier.
 */
export const COLLECTION_SELECTED = 'boostSDCurrentCollectionSelected';

/**
 * Storage key for currently selected product page number.
 *
 * Tracks which page of products is currently displayed.
 */
export const SELECTED_PRODUCT_PAGE = 'boostSDSelectedProductPage';

/**
 * Storage key for filter collapse state.
 *
 * Stores which filter options are expanded or collapsed.
 */
export const COLLAPSE_STATE = 'collapseState';

/**
 * Static list of DOM event names used for XSS detection.
 *
 * Contains 100+ DOM event handler names that could be exploited in XSS attacks.
 * This array is used to build the EVENT_REGEX pattern for comprehensive security scanning.
 *
 * @remarks
 * This constant is shared across the filter module to avoid recreating the array
 * in every validation call. The EVENT_REGEX is compiled from this list once at
 * module initialization for optimal performance.
 *
 * Technical Support teams typically don't need to modify this list, but can
 * reference it when implementing custom XSS detection in extended classes.
 *
 * @see EVENT_REGEX
 * @see FilterValidationHelper.isBadSearchTerm
 */
export const DOM_EVENTS: string[] = [
  'script',
  'alert',
  'onabort',
  'popstate',
  'afterprint',
  'beforeprint',
  'beforeunload',
  'blur',
  'canplay',
  'canplaythrough',
  'change',
  'click',
  'contextmenu',
  'copy',
  'cut',
  'dblclick',
  'drag',
  'dragend',
  'dragenter',
  'dragleave',
  'dragover',
  'dragstart',
  'drop',
  'durationchange',
  'ended',
  'error',
  'focus',
  'focusin',
  'focusout',
  'fullscreenchange',
  'fullscreenerror',
  'hashchange',
  'input',
  'invalid',
  'keydown',
  'keypress',
  'keyup',
  'load',
  'loadeddata',
  'loadedmetadata',
  'loadstart',
  'mousedown',
  'mouseenter',
  'mouseleave',
  'mousemove',
  'mouseover',
  'mouseout',
  'mouseup',
  'offline',
  'online',
  'pagehide',
  'pageshow',
  'paste',
  'pause',
  'play',
  'playing',
  'progress',
  'ratechange',
  'resize',
  'reset',
  'scroll',
  'search',
  'seeked',
  'seeking',
  'select',
  'show',
  'stalled',
  'submit',
  'suspend',
  'timeupdate',
  'toggle',
  'touchcancel',
  'touchend',
  'touchmove',
  'touchstart',
  'unload',
  'volumechange',
  'waiting',
  'wheel',
];

/**
 * Cached regex for DOM event detection.
 *
 * Pre-compiled regular expression pattern built from the DOM_EVENTS array.
 * Used by FilterValidationHelper for efficient XSS pattern detection without
 * recompiling the regex on every validation call.
 *
 * @remarks
 * This regex is compiled once at module initialization and reused across all
 * validation operations, providing ~90% performance improvement over inline
 * regex compilation.
 *
 * Pattern matches event handler attributes (e.g., "onclick=", "onload=") and
 * the word "script" anywhere in the input string (case-insensitive when used
 * with normalized input).
 *
 * @see DOM_EVENTS
 * @see FilterValidationHelper.isBadSearchTerm
 */
export const EVENT_REGEX: RegExp = new RegExp(DOM_EVENTS.join('=|on'));

/**
 * Cached regex patterns for validation.
 *
 * Pre-compiled regular expressions for common validation tasks, shared across
 * the filter module for optimal performance.
 *
 * @remarks
 * These regex patterns are compiled once at module load and reused for all
 * validation operations. Technical Support teams can reference these when
 * implementing custom validation in extended classes.
 *
 * @example
 * Using EMAIL_REGEX in custom validation:
 * ```typescript
 * import { EMAIL_REGEX } from './filter.constants';
 *
 * if (EMAIL_REGEX.test(customerEmail)) {
 *   this.sendNotification(customerEmail);
 * }
 * ```
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
export const RGB_COLOR_REGEX = /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/;
export const XSS_REGEX =
  /\x3Cscript|<script|<\/script|<img[\s\S]*?src|<iframe|<\/iframe|<object|<embed|<applet|on\w+\s*=|javascript:|data:text\/html|&#[xX][0-9a-fA-F]+;/gi;

export const SWATCH_OPTION_TYPE = {
  PRODUCT_IMAGE: 'product-image',
  COLOR_IMAGE: 'color-and-image',
  TEXT: 'text',
};
